import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ChartBar, Storefront, Stack, SignOut, Lock, Buildings } from "@phosphor-icons/react";
import StoreList from "@/pages/StoreList";
import StoreDetail from "@/pages/StoreDetail";
import Aggregate from "@/pages/Aggregate";
import Klienti from "@/pages/Klienti";
import KlientDetail from "@/pages/KlientDetail";
import Login from "@/pages/Login";
import TidoUvitanie from "@/pages/TidoUvitanie";
import AdminTidoCredentials from "@/pages/AdminTidoCredentials";
import NotFound from "@/pages/NotFound";
import TidoEnvBadge from "@/components/TidoEnvBadge";
import { AuthProvider, useAuth } from "@/lib/auth";

function Header() {
  const location = useLocation();
  const isKlienti = location.pathname === "/" || location.pathname.startsWith("/klienti");
  const isStores = location.pathname === "/strediska";
  const isAgg = location.pathname === "/agregat";
  const { user, logout, lockedIdp, lockedStoreId } = useAuth();
  const idpLocked = !!lockedIdp;
  const isDeveloper = user?.role === "developer";
  return (
    <header className="border-b border-[var(--border-default)] bg-white">
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
        <Link
          to={idpLocked && lockedStoreId ? `/strediska/${lockedStoreId}${window.location.search}` : "/"}
          data-testid="header-home-link"
          className="flex items-center gap-3 group"
        >
          <div className="w-9 h-9 rounded-md bg-[var(--brand)] text-white flex items-center justify-center">
            <ChartBar size={20} weight="duotone" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-base font-bold tracking-tight">Hodinový výkon & Vyťaženosť</span>
            <span className="label-eyebrow text-[0.6rem]">Analýza výstupov z dochádzkového systému TiDo</span>
          </div>
        </Link>
        <nav className="flex items-center gap-2 text-sm">
          <TidoEnvBadge />
          {!idpLocked && (
            <>
              <Link
                to="/"
                data-testid="nav-klienti-link"
                className={`px-3 py-2 rounded-md transition-colors ${
                  isKlienti ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
                }`}
                title="Klienti (TiDo firmy)"
              >
                <span className="inline-flex items-center gap-2"><Buildings size={16} /> Klienti</span>
              </Link>
              <Link
                to="/strediska"
                data-testid="nav-stores-link"
                className={`px-3 py-2 rounded-md transition-colors ${
                  isStores ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
                }`}
              >
                <span className="inline-flex items-center gap-2"><Storefront size={16} /> Strediská</span>
              </Link>
              <Link
                to="/agregat"
                data-testid="nav-aggregate-link"
                className={`px-3 py-2 rounded-md transition-colors ${
                  isAgg ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]" : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
                }`}
              >
                <span className="inline-flex items-center gap-2"><Stack size={16} /> Agregát</span>
              </Link>
            </>
          )}
          {idpLocked && (
            <div className="px-3 py-1.5 rounded-md bg-[var(--brand)]/8 text-[var(--brand)] text-xs inline-flex items-center gap-1.5" data-testid="idp-locked-badge">
              <Lock size={12} weight="bold" /> IDP {lockedIdp}
            </div>
          )}
          {user && (
            <div className="ml-3 pl-3 border-l border-[var(--border-default)] flex items-center gap-2">
              <div className="text-right leading-tight">
                <div className="text-xs font-semibold" data-testid="user-name">{user.user_name || user.user_id}</div>
                <div className="text-[10px] text-[var(--text-muted)]">{user.company_name || (user.role ? user.role : "—")}</div>
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded hover:bg-[var(--bg-surface-hover)]"
                data-testid="logout-btn"
                title="Odhlásiť sa"
              >
                <SignOut size={16} />
              </button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}

function TidoContextBanner() {
  const { tidoContext, clearTidoContext } = useAuth();
  if (!tidoContext || (!tidoContext.idp && !tidoContext.rok && !tidoContext.mesiac)) return null;
  const monthNames = ["", "Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December"];
  const mesiacName = tidoContext.mesiac ? monthNames[parseInt(tidoContext.mesiac)] || tidoContext.mesiac : null;
  return (
    <div className="bg-[var(--brand)]/8 border-b border-[var(--brand)]/20" data-testid="tido-context-banner">
      <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center gap-4 text-xs">
        <span className="font-semibold text-[var(--brand)]">Prišiel si z TiDo grafu:</span>
        <div className="flex flex-wrap gap-3 text-[var(--text-secondary)] tabular">
          {tidoContext.idp && <span data-testid="tido-ctx-idp"><strong>IDP</strong> (center_id): {tidoContext.idp}</span>}
          {tidoContext.rok && <span data-testid="tido-ctx-rok"><strong>ROK</strong>: {tidoContext.rok}</span>}
          {tidoContext.mesiac && <span data-testid="tido-ctx-mesiac"><strong>MESIAC</strong>: {tidoContext.mesiac}{mesiacName ? ` (${mesiacName})` : ""}</span>}
          {tidoContext.type && <span data-testid="tido-ctx-type"><strong>type</strong>: {tidoContext.type}</span>}
        </div>
        <button
          onClick={clearTidoContext}
          className="ml-auto text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          data-testid="tido-ctx-clear"
          title="Skryť"
        >
          ✕ zrušiť
        </button>
      </div>
    </div>
  );
}

/**
 * IDP guard — routes for users locked to a single IDP:
 *  - `/` and `/agregat` → redirect to `/strediska/{lockedStoreId}` (with preserved URL params)
 *  - `/strediska/:storeId` where storeId !== lockedStoreId → redirect to the correct one
 *
 * If IDP is present in URL but the store hasn't been resolved yet (no locked_store_id),
 * we redirect the user to `/tido-uvitanie` where data is fetched and store_id is resolved.
 */
function IdpGuardedRoutes() {
  const { lockedIdp, lockedStoreId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // If IDP is set but we don't yet know the store_id, kick to /tido-uvitanie.
  useEffect(() => {
    if (!lockedIdp) return;
    if (location.pathname === "/tido-uvitanie") return;
    if (!lockedStoreId) {
      navigate(`/tido-uvitanie${location.search}`, { replace: true });
    }
  }, [lockedIdp, lockedStoreId, location.pathname, location.search, navigate]);

  const search = location.search || "";

  return (
    <Routes>
      <Route path="/tido-uvitanie" element={<TidoUvitanie />} />
      <Route
        path="/"
        element={
          lockedIdp && lockedStoreId
            ? <Navigate to={`/strediska/${lockedStoreId}${search}`} replace />
            : <Klienti />
        }
      />
      <Route
        path="/klienti/:companyId"
        element={
          lockedIdp && lockedStoreId
            ? <Navigate to={`/strediska/${lockedStoreId}${search}`} replace />
            : <KlientDetail />
        }
      />
      <Route
        path="/strediska"
        element={
          lockedIdp && lockedStoreId
            ? <Navigate to={`/strediska/${lockedStoreId}${search}`} replace />
            : <StoreList />
        }
      />
      <Route
        path="/agregat"
        element={
          lockedIdp && lockedStoreId
            ? <Navigate to={`/strediska/${lockedStoreId}${search}`} replace />
            : <Aggregate />
        }
      />
      <Route
        path="/strediska/:storeId"
        element={<StoreDetailGuarded />}
      />
      <Route
        path="/admin/tido"
        element={
          lockedIdp
            ? <Navigate to={`/strediska/${lockedStoreId || ""}${search}`} replace />
            : <AdminTidoCredentials />
        }
      />
      {/* Legacy route — redirect to primary landing. */}
      <Route path="/admin/klienti" element={<Navigate to="/" replace />} />
      {/* Fallback for any unknown route — MUST be last. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/** Redirect to the locked store if user hit a different one. */
function StoreDetailGuarded() {
  const { lockedIdp, lockedStoreId } = useAuth();
  const location = useLocation();
  // Extract storeId from path
  const storeIdFromPath = location.pathname.split("/")[2];
  if (lockedIdp && lockedStoreId && storeIdFromPath && storeIdFromPath !== lockedStoreId) {
    return <Navigate to={`/strediska/${lockedStoreId}${location.search}`} replace />;
  }
  return <StoreDetail />;
}

function AppShell() {
  const { status, lockedIdp } = useAuth();
  const location = useLocation();

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[var(--text-secondary)]">
        Overujem prístup…
      </div>
    );
  }
  if (status === "denied") {
    return <Login />;
  }
  // /tido-uvitanie has its own full-screen layout; don't wrap it in header/footer.
  const isUvitanie = location.pathname === "/tido-uvitanie";
  if (isUvitanie) {
    return (
      <Routes>
        <Route path="/tido-uvitanie" element={<TidoUvitanie />} />
      </Routes>
    );
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      {!lockedIdp && <TidoContextBanner />}
      <main className="flex-1">
        <IdpGuardedRoutes />
      </main>
      <footer className="border-t border-[var(--border-default)] py-4 px-6 text-center text-xs text-[var(--text-muted)]">
        Hodinový výkon & Vyťaženosť · Analýza výstupov z dochádzkového systému TiDo
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
